/**
 * 방·추가공간 목록 → ItemPicker 섹션.
 *
 * 프로덕션의 `buildObservingPickerSections` / `buildAdditionalSpacePickerEntries` 에
 * 대응한다. 두 화면이 같은 규칙(3섹션 분류, 타입별 이모지, 완료 배지)을 쓰는데 어댑터가
 * 둘로 나뉘어 있어 규칙도 두 벌이었다.
 *
 * 목록 자체를 만드는 일(상담 스냅샷을 읽어 어떤 방·공간이 있는지 알아내기)은 호스트
 * 몫이다 — 프로덕션은 sketching 세션을, 프리뷰는 목 상담을 읽는다. 여기 있는 건 그
 * 목록을 화면에 어떻게 놓을지다.
 */

/** 고를 수 있는 자리 하나. 방이든 추가공간이든 화면에서는 같은 카드다. */
export interface PickEntry {
  id: string;
  label: string;
  /** 프로덕션 룸 타입 (`LIVING_ROOM` / `ROOM` / `BALCONY` …). */
  type?: string;
  /** 카드 아래 보조 텍스트. */
  description?: string;
  /** 짐 기록·영상이 끝난 자리. 카드에 완료 배지가 붙는다. */
  done?: boolean;
  /** 아직 서버 반영을 기다리는 자리. picker 가 진행 표시를 띄운다. */
  pending?: boolean;
}

/** 프로덕션 `ROOM_ICON_BY_TYPE`. */
const ICON_BY_TYPE: Record<string, string> = {
  ENTRANCE: "🚪",
  LIVING_ROOM: "🛋️",
  KITCHEN: "🍳",
  // 🛌(자는 사람 + 침대) — 단순 침대 🛏️ 보다 다수 환경에서 컬러 글리프로 안정적이다.
  ROOM: "🛌",
  BATH_ROOM: "🚿",
  BALCONY: "🪟",
  RESIDENCE_STORAGE: "📦",
  OTHER_SPACE: "📦",
};

const PRIMARY_TYPES = new Set(["LIVING_ROOM", "KITCHEN", "BATH_ROOM"]);
const ROOM_TYPES = new Set(["ROOM"]);
/** 현관은 어느 섹션에도 넣지 않는다 — 규칙이 자동으로 먼저 기록시키므로 고를 일이 없다. */
const EXCLUDED_TYPES = new Set(["ENTRANCE"]);

const SECTION_TITLES: Record<"primary" | "rooms" | "additional", string> = {
  primary: "주요공간",
  rooms: "방",
  additional: "추가공간",
};

export interface PickerSection {
  id: string;
  title: string;
  items: Array<Record<string, unknown>>;
}

function toItem(entry: PickEntry): Record<string, unknown> {
  return {
    id: entry.id,
    title: entry.label,
    icon: ICON_BY_TYPE[entry.type ?? ""] ?? "📦",
    // 카드를 누르면 이 값이 `$event` 로 실려 다음 화면(짐 기록)의 방 타입이 된다.
    roomType: entry.type ?? "OTHER_SPACE",
    ...(entry.description ? { description: entry.description } : {}),
    ...(entry.pending ? { pending: true } : {}),
  };
}

/**
 * 방은 세 섹션으로 나누고, 추가공간은 한 섹션에 모은다.
 *
 * 빈 섹션은 내보내지 않는다 — 시각적 노이즈를 줄이기 위함.
 */
export function pickerSections(
  entries: readonly PickEntry[],
  layout: "room" | "additional",
): PickerSection[] {
  const visible = entries.filter((e) => !EXCLUDED_TYPES.has(e.type ?? ""));

  if (layout === "additional") {
    return visible.length > 0
      ? [{ id: "additional-space", title: "추가공간", items: visible.map(toItem) }]
      : [];
  }

  const bucket = (e: PickEntry): "primary" | "rooms" | "additional" =>
    PRIMARY_TYPES.has(e.type ?? "")
      ? "primary"
      : ROOM_TYPES.has(e.type ?? "")
        ? "rooms"
        : "additional";

  return (["primary", "rooms", "additional"] as const)
    .map((id) => ({
      id,
      title: SECTION_TITLES[id],
      items: visible.filter((e) => bucket(e) === id).map(toItem),
    }))
    .filter((s) => s.items.length > 0);
}

/**
 * 완료된 자리의 배지용 인스턴스.
 *
 * navigation 모드의 ItemPicker 는 인스턴스가 하나라도 있는 카드를 완료로 그린다.
 * 프로덕션 `entriesToDoneInstances` 와 같은 방식이다.
 */
export function pickerDoneInstances(
  entries: readonly PickEntry[],
): Array<{ instanceId: string; itemId: string; answers: Record<string, unknown> }> {
  return entries
    .filter((e) => e.done)
    .map((e) => ({ instanceId: `done_${e.id}`, itemId: e.id, answers: {} }));
}
